/**
 * Career Stage Attendance Utilities
 *
 * Calculates potential live attendees based on artist career stages
 * Uses lookup table data to estimate attendance for marketing projections
 */

import type { ShowWithDetails } from "./types/shows.types";

/**
 * Career stage to average attendees mapping
 * Based on typical venue capacities for each artist tier
 *
 * Data source: career_stage_attendance table
 */
const CAREER_STAGE_ATTENDANCE: Record<string, number> = {
  // Entry level artists
  "Developing": 250,
  "Local": 250,
  "Emerging": 500,
  "Niche": 500, // Alternative name for Emerging

  // Established tiers
  "Established 1": 900,
  "Established 2": 1400,
  "Established 3": 2100,
  "Established1": 900,  // Handle no-space variant
  "Established2": 1400,
  "Established3": 2100,

  // Mainstream tiers
  "Mainstream 1": 3200,
  "Mainstream 2": 5700,
  "Mainstream 3": 10400,
  "Mainstream 4": 17200,
  "Mainstream1": 3200,  // Handle no-space variant
  "Mainstream2": 5700,
  "Mainstream3": 10400,
  "Mainstream4": 17200,
};

/**
 * Default attendee count for unknown career stages
 */
const DEFAULT_ATTENDEES = 250;

/**
 * Normalizes career stage string to handle variations
 * @param careerStage - Raw career stage string from database
 * @returns Normalized career stage string
 */
function normalizeCareerStage(careerStage: string | null | undefined): string {
  if (!careerStage) return "";

  // Remove extra whitespace and normalize case
  const normalized = careerStage.trim();

  // Try exact match first
  if (CAREER_STAGE_ATTENDANCE[normalized]) {
    return normalized;
  }

  // Try case-insensitive match
  const careerStageKey = Object.keys(CAREER_STAGE_ATTENDANCE).find(
    key => key.toLowerCase() === normalized.toLowerCase()
  );

  return careerStageKey || normalized;
}

/**
 * Gets average attendee count for a specific career stage
 * @param careerStage - Artist career stage
 * @returns Average attendee count for this career stage
 */
export function getAttendeesByCareerStage(careerStage: string | null | undefined): number {
  const normalized = normalizeCareerStage(careerStage);
  return CAREER_STAGE_ATTENDANCE[normalized] || DEFAULT_ATTENDEES;
}

/**
 * Calculates total potential live attendees for a set of shows
 *
 * Algorithm:
 * 1. Group shows by artist ID
 * 2. For each artist, get their career stage
 * 3. Look up average attendees for that career stage
 * 4. Multiply avg attendees × number of shows for that artist
 * 5. Sum across all artists
 *
 * @param shows - Array of shows with artist details
 * @returns Total potential live attendees across all shows
 */
export function calculatePotentialAttendees(shows: ShowWithDetails[]): number {
  if (!shows || shows.length === 0) {
    return 0;
  }

  // Group shows by artist ID to handle multiple shows per artist
  const showsByArtist = shows.reduce((acc, show) => {
    const artistId = show.artist.id;
    if (!acc[artistId]) {
      acc[artistId] = {
        artist: show.artist,
        showCount: 0
      };
    }
    acc[artistId].showCount++;
    return acc;
  }, {} as Record<number, { artist: any, showCount: number }>);

  // Calculate total attendees
  const totalAttendees = Object.values(showsByArtist).reduce((total, { artist, showCount }) => {
    const avgAttendees = getAttendeesByCareerStage(artist.careerStage);
    const artistTotal = avgAttendees * showCount;
    return total + artistTotal;
  }, 0);

  return totalAttendees;
}

/**
 * Calculates average potential attendees per show
 * @param shows - Array of shows with artist details
 * @returns Average potential attendees per show
 */
export function calculateAvgAttendeesPerShow(shows: ShowWithDetails[]): number {
  if (!shows || shows.length === 0) {
    return 0;
  }

  const total = calculatePotentialAttendees(shows);
  return Math.round(total / shows.length);
}

/**
 * Gets breakdown of potential attendees by career stage
 * Useful for detailed reporting
 *
 * @param shows - Array of shows with artist details
 * @returns Object mapping career stages to attendee counts
 */
export function getAttendeeBreakdownByCareerStage(shows: ShowWithDetails[]): Record<string, number> {
  if (!shows || shows.length === 0) {
    return {};
  }

  const breakdown: Record<string, number> = {};

  // Group shows by artist ID
  const showsByArtist = shows.reduce((acc, show) => {
    const artistId = show.artist.id;
    if (!acc[artistId]) {
      acc[artistId] = {
        artist: show.artist,
        showCount: 0
      };
    }
    acc[artistId].showCount++;
    return acc;
  }, {} as Record<number, { artist: any, showCount: number }>);

  // Calculate attendees by career stage
  Object.values(showsByArtist).forEach(({ artist, showCount }) => {
    const careerStage = normalizeCareerStage(artist.careerStage) || "Unknown";
    const avgAttendees = getAttendeesByCareerStage(artist.careerStage);
    const stageTotal = avgAttendees * showCount;

    breakdown[careerStage] = (breakdown[careerStage] || 0) + stageTotal;
  });

  return breakdown;
}
